import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ArrowRight, Check, X } from "lucide-react";
import { cn } from "@/lib/utils";

const CARD_W = 360;
const PAD = 10;

/**
 * Spotlight product tour. Dims the app, cuts a hole around the real element for
 * the current step and anchors an explanation card next to it.
 */
export default function ProductTour({ steps, open, onFinish, onSkip }) {
  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const raf = useRef(null);

  const step = steps[index];

  useEffect(() => { if (open) setIndex(0); }, [open]);

  // Navigate to the page the step lives on
  useEffect(() => {
    if (!open || !step?.route) return;
    if (location.pathname !== step.route) navigate(step.route);
  }, [open, step?.route, location.pathname, navigate]);

  const measure = useCallback(() => {
    if (!step) return;
    if (!step.target) { setRect(null); return; }
    const el = document.querySelector(step.target);
    if (!el) { setRect(null); return; }
    const r = el.getBoundingClientRect();
    setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
  }, [step]);

  // Poll briefly for the target (pages load async), then keep it in sync
  useLayoutEffect(() => {
    if (!open || !step) return;
    let tries = 0;
    let scrolled = false;
    const tick = () => {
      const el = step.target ? document.querySelector(step.target) : null;
      if (el && !scrolled) {
        const r = el.getBoundingClientRect();
        const needsScroll = r.top < 0 || r.bottom > window.innerHeight;
        if (needsScroll && r.height < window.innerHeight * 0.7) {
          el.scrollIntoView({ block: "center", behavior: "smooth" });
        }
        scrolled = true;
      }
      measure();
      tries += 1;
      if ((el && tries > 8) || tries > 26) { clearInterval(id); }
    };
    const id = setInterval(tick, 120);
    tick();
    return () => clearInterval(id);
  }, [open, step, index, measure]);

  useEffect(() => {
    if (!open) return;
    const onMove = () => {
      cancelAnimationFrame(raf.current);
      raf.current = requestAnimationFrame(measure);
    };
    window.addEventListener("resize", onMove);
    window.addEventListener("scroll", onMove, true);
    return () => {
      window.removeEventListener("resize", onMove);
      window.removeEventListener("scroll", onMove, true);
      cancelAnimationFrame(raf.current);
    };
  }, [open, measure]);

  const next = useCallback(() => {
    if (index >= steps.length - 1) onFinish?.();
    else setIndex((i) => i + 1);
  }, [index, steps.length, onFinish]);
  const back = useCallback(() => setIndex((i) => Math.max(0, i - 1)), []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onSkip?.();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") back();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, next, back, onSkip]);

  if (!open || !step) return null;

  // Card placement — below the target if there's room, else above, else centred
  const vh = window.innerHeight;
  const vw = window.innerWidth;
  let cardStyle = { top: vh / 2 - 110, left: vw / 2 - CARD_W / 2 };
  if (rect) {
    const tall = rect.height > vh * 0.55;
    if (tall) {
      // Sit beside a full-height target (sidebar, long page) rather than above it
      const leftSide = rect.left + rect.width / 2 < vw / 2;
      cardStyle = {
        top: Math.max(24, Math.min(vh / 2 - 120, vh - 260)),
        left: leftSide
          ? Math.min(rect.left + rect.width + PAD * 2, vw - CARD_W - 16)
          : Math.max(16, rect.left - CARD_W - PAD * 2),
      };
    } else {
      const below = rect.top + rect.height + PAD;
      const wantAbove = below + 230 > vh;
      cardStyle = {
        top: wantAbove ? Math.max(16, rect.top - 230 - PAD) : below,
        left: Math.min(Math.max(16, rect.left + rect.width / 2 - CARD_W / 2), vw - CARD_W - 16),
      };
    }
  }

  return (
    <div className="fixed inset-0 z-[200]" data-testid="product-tour">
      {/* Spotlight — the ring plus a giant shadow that dims everything else */}
      {rect ? (
        <div
          className="absolute rounded-xl pointer-events-none transition-all duration-300 ring-2 ring-[hsl(var(--studio-primary))]"
          style={{
            top: rect.top - 6,
            left: rect.left - 6,
            width: rect.width + 12,
            height: rect.height + 12,
            boxShadow: "0 0 0 9999px rgba(4, 8, 20, 0.62)",
          }}
          data-testid="tour-spotlight"
        />
      ) : (
        <div className="absolute inset-0 bg-[rgba(4,8,20,0.62)]" />
      )}

      {/* Click-catcher so the app isn't interactive mid-tour */}
      <div className="absolute inset-0" onClick={(e) => e.stopPropagation()} />

      <div
        className="absolute rounded-2xl border border-border bg-card shadow-2xl p-5 animate-in fade-in zoom-in-95 duration-200"
        style={{ ...cardStyle, width: CARD_W }}
        data-testid="tour-card"
      >
        <div className="flex items-start gap-3">
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--studio-primary))] font-medium">
              {step.chapter || "Mahano OS tour"} · {index + 1}/{steps.length}
            </div>
            <div className="text-base font-semibold tracking-tight mt-1" data-testid="tour-title">{step.title}</div>
            <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed" data-testid="tour-body">{step.body}</p>
          </div>
          <button onClick={onSkip} data-testid="tour-skip" className="text-muted-foreground hover:text-foreground shrink-0" title="Skip tour">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex items-center gap-1.5 mt-4" data-testid="tour-progress">
          {steps.map((s, i) => (
            <button
              key={s.id}
              onClick={() => setIndex(i)}
              data-testid={`tour-dot-${i}`}
              className={cn(
                "h-1.5 rounded-full transition-all",
                i === index ? "w-6 bg-[hsl(var(--studio-primary))]" : "w-1.5 bg-border hover:bg-muted-foreground/40"
              )}
              aria-label={`Step ${i + 1}`}
            />
          ))}
        </div>

        <div className="flex items-center gap-2 mt-4">
          <Button variant="ghost" size="sm" onClick={onSkip} data-testid="tour-skip-text" className="text-muted-foreground">
            Skip
          </Button>
          <div className="ml-auto flex items-center gap-2">
            {index > 0 && (
              <Button variant="outline" size="sm" onClick={back} data-testid="tour-back">
                <ArrowLeft className="w-3.5 h-3.5 mr-1.5" /> Back
              </Button>
            )}
            <Button size="sm" onClick={next} data-testid="tour-next"
              className="bg-[hsl(var(--studio-primary))] hover:bg-[hsl(var(--studio-primary-hover))] text-white">
              {index === steps.length - 1
                ? <><Check className="w-3.5 h-3.5 mr-1.5" /> Finish</>
                : <>Next <ArrowRight className="w-3.5 h-3.5 ml-1.5" /></>}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
